import { IconName } from '@lyse-as/formds-icons';
import { Component, h, Prop, Watch } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';

import state from '@/store';

@Component({
  tag: 'fds-nav-list-item',
  styleUrl: 'styles/nav-list-item.scss',
  shadow: false,
})
export class NavListItem {
  private itemId = uuidv4();

  /**
   * The header text of the component
   */
  @Prop() header!: string;

  /**
   * The secondary header text of the component. This should never be used together with description. It's either or
   */
  @Prop() secondaryHeader: string;

  @Watch('secondaryHeader')
  validateSecondaryHeader(value: string) {
    this.validateSecondaryHeaderAndDescription(value, this.description);
  }

  /**
   * The description text of the component. This should never be used together with secondary header. It's either or
   */
  @Prop() description: string;

  @Watch('description')
  validateDescription(value: string) {
    this.validateSecondaryHeaderAndDescription(this.description, value);
  }

  /**
   * The icon to render at the left-hand side of the header text
   */
  @Prop() headerIcon: IconName;

  /**
   * The color of the icon at the left-hand side of the header text
   */
  @Prop() headerIconColor: string;

  /**
   * The text that will be rendered at the right hand side of the component
   */
  @Prop() navText: string;

  /**
   * The icon that will be rendered next to the navText
   */
  @Prop() navIcon: IconName;

  /**
   * If true, the component will be disabled and users cannot interact with it
   */
  @Prop() disabled: boolean;

  /**
   * Sets the href attribute, i.e. the URL, to navigate to on click
   */
  @Prop() href!: string;

  /** @internal */
  @Prop() bordered = false;

  /** @internal */
  @Prop() first = false;

  /** @internal */
  @Prop() last = false;

  /** @internal */
  @Prop() hideTopBorder = false;

  /**
   * Navigate within the application without refreshing the page
   */
  @Prop() preventReload?: boolean;

  private handleClick = (event: MouseEvent) => {
    if (this.preventReload) {
      event.preventDefault();
      window.history.pushState({}, '', this.href);
      window.dispatchEvent(new Event('popstate'));
    }
  };

  componentDidLoad() {
    this.validateSecondaryHeaderAndDescription(this.secondaryHeader, this.description);
  }

  private validateSecondaryHeaderAndDescription(secondaryHeader: string, description: string) {
    if (secondaryHeader && description) {
      throw new Error("You can't provide both a secondary header and a description. Use only one!");
    }
  }

  render() {
    return (
      <li
        id={this.itemId}
        role="none"
        class={`fds-nav-list-item ${this.disabled ? 'fds-nav-list-item--disabled' : ''} ${state.mode} ${
          this.bordered ? 'fds-nav-list-item--bordered' : ''
        } ${this.first ? 'fds-nav-list-item--first' : ''} ${this.last ? 'fds-nav-list-item--last' : ''} ${
          this.hideTopBorder ? 'fds-nav-list-item--hide-top-border' : ''
        }`}
      >
        <a
          href={this.href}
          role="menuitem"
          aria-disabled={this.disabled ? 'true' : 'false'}
          tabIndex={this.disabled ? -1 : 0}
          class={`fds-nav-list-item__link ${this.disabled ? 'fds-nav-list-item__link--disabled' : ''} ${this.headerIcon ? 'fds-nav-list-item__link--headerIcon' : ''}`}
          onClick={this.handleClick}
        >
          <div class="fds-nav-list-item__header-icon">
            {this.headerIcon && <fds-icon name={this.headerIcon} color={this.headerIconColor} />}
          </div>
          <div class="fds-nav-list-item__header-container">
            <h3 class="fds-nav-list-item__header">{this.header}</h3>
          </div>
          <div class="fds-nav-list-item__nav">
            {this.navText && <span>{this.navText}</span>}
            {this.navIcon && <fds-icon name={this.navIcon} />}
          </div>
          <div class="fds-nav-list-item__empty-header-icon"></div>
          <div class="fds-nav-list-item__secondary-header-and-description">
            {this.secondaryHeader && <span class="fds-nav-list-item__secondary-header">{this.secondaryHeader}</span>}
            {!this.secondaryHeader && this.description && (
              <span class="fds-nav-list-item__description">{this.description}</span>
            )}
          </div>
          <div class="fds-nav-list-item__empty-nav"></div>
        </a>
      </li>
    );
  }
}
